<template>
  <AppShell title="Redigera ansökan">
    <div class="grid gap-3">
      <ApplicationMetadataBox v-if="hasLoadedApplication" :type-label="translateRef('applicationTypes', form.type)" />

      <AppCard>
        <p v-if="isLoadingApplication" class="text-subtle">Hämtar ansökan...</p>
        <ApplicationErrorBox v-else-if="!hasLoadedApplication" :message="error" />
        <AppValidationGroup v-else v-slot="{ validate }">
          <form class="grid gap-6" @submit.prevent="validateFor('submit', validate, () => void onSubmit())">
            <ApplicationFormSections :sections="applicationForm.sections" :questions="questions" />

            <ApplicationErrorBox v-if="error" :message="error" :details="errorDetails" :debug="errorDebug" />

            <p v-if="success" class="success-text">{{ success }}</p>

            <div class="flex justify-end gap-3">
              <AppButton
                type="button"
                :disabled="isSubmitting || !canSaveDraft"
                large
                @click="validateFor('draft', validate, () => void onUpdateDraft())"
              >
                {{ isSubmitting && submitAction === 'draft' ? 'Sparar...' : 'Spara utkast' }}
              </AppButton>
              <AppButton type="submit" :disabled="isSubmitting" color="green" large>
                {{ isSubmitting && submitAction === 'submit' ? 'Skickar...' : 'Skicka in ansökan' }}
              </AppButton>
            </div>
          </form>
        </AppValidationGroup>
      </AppCard>
    </div>
  </AppShell>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { AppButton, AppCard, AppValidationGroup } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import { useMetadata } from '@/composables/useMetadata'
import { fetchApplicationFormById, submitApplication, updateApplication } from '@/services/application'
import ApplicationErrorBox from '../components/ApplicationErrorBox.vue'
import ApplicationFormSections from '../components/ApplicationFormSections.vue'
import ApplicationMetadataBox from '../components/ApplicationMetadataBox.vue'
import { getApplicationForm } from '../forms'
import { questions } from '../questions'
import { buildUpdatePayload, hydrateApplicationForm } from '../applicationFormMapper'
import { useApplicationFormData } from '../composables/applicationFormData'
import { useEditApplicationDraft } from '../composables/useEditApplicationDraft'

const { translateRef } = useMetadata()
const { form, validateFor } = useApplicationFormData()

const {
  isSubmitting,
  isLoadingApplication,
  hasLoadedApplication,
  submitAction,
  error,
  errorDetails,
  errorDebug,
  success,
  canSaveDraft,
  onUpdateDraft,
  onSubmit,
} = useEditApplicationDraft({
  fetchForm: fetchApplicationFormById,
  hydrate: hydrateApplicationForm,
  update: (id) => updateApplication(id, buildUpdatePayload(form)),
  submit: async (id) => {
    await submitApplication(id)
  },
  redirectTo: (id) => `/ansokningar/${id}`,
  messages: {
    loadFailed: 'Kunde inte hämta ansökan.',
    saveFailed: 'Kunde inte spara ansökan.',
    draftSaved: (id) => `Ansökan ${id} har sparats.`,
  },
})

const applicationForm = computed(() => getApplicationForm(form.type))
</script>

<style scoped lang="scss">
.success-text {
  color: var(--color-success-fg);
}
</style>
